<script setup lang="ts">
/**
 * Doors and drawers in Shape (src/families/fronts.ts): pick one opening, or several that make a
 * rectangle, then choose what covers them — a door, a pair, a glazed door, sliding doors or drawers.
 * Each front then lists its own choices (overlay or inset, hinge side, how many drawers, the pull) and
 * a remove button. Problems show beside the list with their one-click fix.
 */
import { computed, watch } from 'vue';
import type { ParameterDef } from '@/families/types';
import { frontsOf, type FrontKind, type FrontSpec } from '@/families/fronts';
import Icon from './Icon.vue';
import { snap, ui } from '@/app/state';
import { setFronts } from '@/app/edits';
import { applyIssueFix } from '@/app/fixes';

defineProps<{ p: ParameterDef }>();
const design = computed(() => snap.value.design);
const build = computed(() => snap.value.build);
const fronts = computed<FrontSpec[]>(() => frontsOf(design.value as never));
const openings = computed(() => build.value.openings.map((o) => ({ id: o.id, label: o.label })));
const labelOf = (id: string) => openings.value.find((o) => o.id === id)?.label ?? id;
const frontAt = (id: string) => fronts.value.findIndex((f) => f.openings.includes(id));

const selected = computed({ get: () => ui.frontSelection, set: (ids: string[]) => { ui.frontSelection = ids; } });
watch(openings, (items) => { selected.value = selected.value.filter((id) => items.some((o) => o.id === id)); });
function toggle(id: string) {
  selected.value = selected.value.includes(id) ? selected.value.filter((x) => x !== id) : [...selected.value, id];
}
/** In the openings' own order (bottom to top), so the anchor is the first. */
const picked = computed(() => openings.value.map((o) => o.id).filter((id) => selected.value.includes(id)));

const KIND_TEXT: Record<FrontKind, string> = { door: 'Door', glazed: 'Glazed door', sliding: 'Sliding doors', drawers: 'Drawers' };
function describe(f: FrontSpec): string {
  const what = f.kind === 'drawers' ? `${f.drawers ?? 1} drawer${(f.drawers ?? 1) === 1 ? '' : 's'}` : f.kind === 'sliding' ? 'Sliding doors' : `${f.hinge === 'pair' ? 'Pair of ' : ''}${f.kind === 'glazed' ? 'glazed door' : 'door'}${f.hinge === 'pair' ? 's' : ''}`;
  const where = f.openings.length > 1 ? `${labelOf(f.openings[0]!)} + ${f.openings.length - 1}` : labelOf(f.openings[0]!);
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} · ${where}`;
}

/** Cover the picked openings with a new front, replacing any front already over one of them. */
function cover(kind: FrontKind, pair = false) {
  const ids = picked.value;
  if (!ids.length) return;
  const keep = fronts.value.filter((f) => !f.openings.some((id) => ids.includes(id)));
  const spec: FrontSpec = { openings: ids, kind, mount: 'overlay', pull: 'knob', ...(kind === 'door' || kind === 'glazed' ? { hinge: pair ? 'pair' : 'left' } : {}), ...(kind === 'drawers' ? { drawers: 1 } : {}) };
  setFronts([...keep, spec], `${pair ? 'Pair of doors' : KIND_TEXT[kind]} on ${ids.length > 1 ? `${ids.length} openings` : labelOf(ids[0]!)}`);
  selected.value = [];
}
function clear() {
  const ids = picked.value;
  setFronts(fronts.value.filter((f) => !f.openings.some((id) => ids.includes(id))), `Fronts removed from ${ids.length > 1 ? `${ids.length} openings` : labelOf(ids[0]!)}`);
  selected.value = [];
}
function join() {
  const existing = fronts.value.find((f) => f.openings.some((id) => picked.value.includes(id)));
  if (!existing || existing.kind === 'drawers') { cover('door'); return; }
  const ids = picked.value;
  setFronts([...fronts.value.filter((f) => !f.openings.some((id) => ids.includes(id))), { ...existing, openings: ids }], `Join ${ids.length} openings under one front`);
  selected.value = [];
}
const canSplit = computed(() => fronts.value.some((f) => f.openings.length > 1 && f.openings.some((id) => picked.value.includes(id))));
function split() {
  setFronts(fronts.value.flatMap((f) => f.openings.length > 1 && f.openings.some((id) => picked.value.includes(id)) ? f.openings.map((id) => ({ ...f, openings: [id] })) : [f]), 'Split spanning fronts into separate openings');
  selected.value = [];
}
function change(i: number, patch: Partial<FrontSpec>, label: string) {
  setFronts(fronts.value.map((f, j) => (j === i ? { ...f, ...patch } : f)), label);
}
function remove(i: number) {
  setFronts(fronts.value.filter((_f, j) => j !== i), `${describe(fronts.value[i]!)} removed`);
}
const problems = computed(() => build.value.validation.errors.filter((e) => e.field === 'fronts'));
const notes = computed(() => build.value.validation.warnings.filter((e) => e.field === 'fronts'));
</script>

<template>
  <div class="fronts-ctl" data-fronts>
    <h3 id="ph-fronts" class="h-sub">{{ p.ui.label }}</h3>
    <p class="hint">{{ p.ui.help }}</p>
    <button type="button" class="btn btn--sm btn--quiet" data-front-pick :aria-pressed="ui.frontPicking" @click="ui.frontPicking = !ui.frontPicking">Pick openings on model</button>
    <p v-if="ui.frontPicking" class="hint">Click an opening to pick it. Shift-click to add or remove openings. Choose a front, Join, or Split below.</p>

    <ol class="fronts-openings" aria-labelledby="ph-fronts">
      <li v-for="o in openings.slice().reverse()" :key="o.id">
        <label class="fronts-op" :data-opening="o.id">
          <input type="checkbox" :checked="selected.includes(o.id)" @change="toggle(o.id)">
          <span>{{ o.label }}</span>
          <span v-if="frontAt(o.id) >= 0" class="chip">{{ describe(fronts[frontAt(o.id)]!).split(' · ')[0] }}</span>
          <span v-else class="muted">open</span>
        </label>
      </li>
    </ol>

    <div v-if="picked.length" class="fronts-actions" role="group" aria-label="Cover the picked openings">
      <button type="button" class="btn btn--sm" data-front="join" :disabled="picked.length < 2" @click="join">Join</button>
      <button type="button" class="btn btn--sm" data-front="split" :disabled="!canSplit" @click="split">Split</button>
      <button type="button" class="btn btn--sm" data-front="door" @click="cover('door')">Door</button>
      <button type="button" class="btn btn--sm" data-front="pair" @click="cover('door', true)">Pair of doors</button>
      <button type="button" class="btn btn--sm" data-front="glazed" @click="cover('glazed')">Glazed door</button>
      <button type="button" class="btn btn--sm" data-front="sliding" @click="cover('sliding')">Sliding doors</button>
      <button type="button" class="btn btn--sm" data-front="drawers" :disabled="picked.length > 1" :title="picked.length > 1 ? 'A drawer stack fills one opening' : ''" @click="cover('drawers')">Drawers</button>
      <button type="button" class="btn btn--sm btn--quiet" data-front="none" @click="clear">Leave open</button>
    </div>

    <ul v-if="fronts.length" class="rowdivs fronts-list">
      <li v-for="(f, i) in fronts" :key="i" class="rowdiv" :data-front-item="i">
        <div class="rowdiv-head">
          <strong>{{ describe(f) }}</strong>
          <button type="button" class="icon-btn icon-btn--sm" :aria-label="`Remove ${describe(f).toLowerCase()}`" @click="remove(i)"><Icon name="close" /></button>
        </div>
        <div class="fronts-opts">
          <div v-if="f.kind !== 'sliding'" class="seg seg--sm" role="radiogroup" :aria-label="`${describe(f)}: how it sits`">
            <button v-for="m in (['overlay', 'inset'] as const)" :key="m" type="button" role="radio" :aria-checked="(f.mount ?? 'overlay') === m" @click="change(i, { mount: m }, `${describe(f)}: ${m}`)">{{ m === 'overlay' ? 'Overlay' : 'Inset' }}</button>
          </div>
          <div v-if="f.kind === 'door' || f.kind === 'glazed'" class="seg seg--sm" role="radiogroup" :aria-label="`${describe(f)}: hinges`">
            <button v-for="h in (['left', 'right', 'pair'] as const)" :key="h" type="button" role="radio" :aria-checked="f.hinge === h" @click="change(i, { hinge: h }, `${describe(f)}: hinged ${h === 'pair' ? 'as a pair' : `on the ${h}`}`)">{{ h === 'pair' ? 'Pair' : h === 'left' ? 'Hinge left' : 'Hinge right' }}</button>
          </div>
          <div v-if="f.kind === 'drawers'" class="stepper" role="group" :aria-label="`${describe(f)}: how many`">
            <button type="button" class="icon-btn" :disabled="(f.drawers ?? 1) <= 1" aria-label="One drawer fewer" @click="change(i, { drawers: (f.drawers ?? 1) - 1, drawerRatios: undefined }, 'One drawer fewer')"><Icon name="minus" /></button>
            <output class="stepper-v">{{ f.drawers ?? 1 }}</output><span class="stepper-u">{{ (f.drawers ?? 1) === 1 ? 'drawer' : 'drawers' }}</span>
            <button type="button" class="icon-btn" :disabled="(f.drawers ?? 1) >= 4" aria-label="One more drawer" @click="change(i, { drawers: (f.drawers ?? 1) + 1, drawerRatios: undefined }, 'One more drawer')"><Icon name="plus" /></button>
          </div>
          <div class="seg seg--sm" role="radiogroup" :aria-label="`${describe(f)}: pull`">
            <button v-for="u in (['knob', 'bar', 'none'] as const)" :key="u" type="button" role="radio" :aria-checked="(f.pull ?? 'knob') === u" @click="change(i, { pull: u }, `${describe(f)}: ${u === 'none' ? 'no pull' : u}`)">{{ u === 'knob' ? 'Knob' : u === 'bar' ? 'Bar' : 'No pull' }}</button>
          </div>
        </div>
      </li>
    </ul>

    <div v-for="(e, k) in problems" :key="`e${k}`" class="field-err fronts-err" role="alert">
      <Icon name="alert" /><span>{{ e.message }}</span>
      <button v-if="e.fix" type="button" class="btn btn--sm" @click="applyIssueFix(e.fix)">{{ e.fix.label }}</button>
    </div>
    <p v-for="(w, k) in notes" :key="`w${k}`" class="hint">{{ w.message }}</p>
  </div>
</template>
